import ThermalPrinterEncoder, {
  ThermalPrinterEncoderOption,
} from "../thermal-printer-encoder"
import moment from "moment/moment"
import { printBorder, printSplitTable } from "./helper"
import { Business, KitchenPrinterOptions } from "../models"
import { formatNumberToCurrency } from "../currency"

export type KitchenPrinterItem = {
  products: Array<{
    id: string
    product: string
    quantity: number
    orderDate: string
    discount: number
    outstanding: number
    taxIncluded: boolean
    name: string
    sellingPrice: number
    originalSellingPrice?: number
    notes?: string
    productOptionValues: Array<{
      id: string
      name: string
      productOptionId: string
      productOptionName: string
    }>
    orderedProductAddOns: Array<{
      name: string
      id: string
      quantity: number
      sellingPrice: number
    }>
    categoryId: string
  }>
  location: {
    birEnabled: boolean
  }
  tax: number
  serviceCharge: number
  discount: number
  totalCartAmount: number
  totalItemQuantity: number
  cashbookTransactionId: string
  creditbookTransactionId: string
  buxRequestId: string
  buxPaymentStatus: string
  orderStatus: string
  customerName: string
  shippingCost: number
  paymentMethodCode: string
  isMpos: boolean
  isServiceOrder: boolean
  business: string
  locationId: string
  outstanding: number
  notes: string
  isWalkIn: boolean
  createdBy: string
  cashReceived: number
  cashChange: number
  cashRegister: string
  taxes: Array<{
    id: string
    rate: number
    name: string
    type: string
    applyTo: string
    option: string
    accountType: any
    amount: number
  }>
  source: string
  reward: any
  cashback: number
  isB2B: boolean
  priceList: string
  wholesale: boolean
  taxExemptions: Array<any>
  isPickup: boolean
  pickupTime: any
  id: string
  orderNumber: string
  receiptHistory: Array<{
    id: string
    seriesNumber: string
    ORNumber: string
    type: string
    createdAt: string
    updatedAt: string
    printed: boolean
  }>
  orderType?: string
  tables?: Array<{
    name: string
  }>
  queueNumber?: string
}

export const encodeKitchenPrinter = (
  trx: KitchenPrinterItem,
  activeUser: string,
  business: Business,
  kitchenPrinterOptions?: KitchenPrinterOptions,
  encoderOptions: ThermalPrinterEncoderOption = {
    language: "esc-pos",
    columns: 32,
  }
) => {
  const width = encoderOptions.columns || 32
  let encoder = new ThermalPrinterEncoder(encoderOptions)
    .initializeWithMultiByte()
    .align("center")
    .bold(true)

  if (kitchenPrinterOptions?.type) {
    if (kitchenPrinterOptions?.type === "REPRINT") {
      encoder = encoder.line("*** REPRINT ***")
    } else if (kitchenPrinterOptions?.type === "EDITED") {
      encoder = encoder.line("*** EDITED ***")
    } else if (kitchenPrinterOptions?.type === "VOIDED") {
      encoder = encoder.line("*** VOIDED ***")
    }
  }

  encoder = encoder.line(`#${trx.orderNumber}`)

  if (kitchenPrinterOptions?.type !== "REPRINT") {
    encoder = encoder.line(`Trans No: 000001`)
  } else {
    encoder = encoder.line(
      `Reprint Date and Time: ${moment(Date.now()).format("D MMM YYYY h:mm a")}`
    )
  }

  encoder = encoder.line(business.name)
  // print table number if available
  if (trx.tables?.[0]?.name) {
    encoder = encoder.line(`Table: ${trx.tables[0].name}`)
  }

  encoder = encoder
    .newline()
    .align("center")
    .bold(false)
    .line(moment(Date.now()).format("D MMM YYYY h:mm a"))

  if (trx.location?.birEnabled) {
    const originalSeriesNumber = trx.receiptHistory?.find(
      (el: any) => el.type === "ORIGINAL"
    )?.seriesNumber
    encoder = encoder.line(`Trans No.: ${originalSeriesNumber}`)
  }

  encoder = encoder.align("left")

  encoder = printBorder(encoder, width, { noSpacing: true })
  encoder = printSplitTable(encoder, width, 8, [
    ["Item(s)", trx.products.length.toString()],
  ])
  encoder = printBorder(encoder, width, { noSpacing: true })

  trx.products.forEach((p) => {
    encoder = encoder.bold(true).line(`${p.quantity} x ${p.name}`).bold(false)

    if (p.productOptionValues) {
      if (kitchenPrinterOptions?.variant === "ONE_LINE") {
        encoder = encoder.line(
          p.productOptionValues
            .map((v) => `${v.productOptionName}: ${v.name}`)
            .join(", ")
        )
      } else {
        p.productOptionValues.forEach((v) => {
          encoder = encoder.line(`${v.productOptionName}: ${v.name}`)
        })
      }
    }

    if (p.orderedProductAddOns?.length) {
      encoder = encoder.line("Add-ons")
      p.orderedProductAddOns.forEach(({ name, quantity }) => {
        encoder = encoder.line(`- ${name} x ${quantity}`)
      })
    }

    if (p.notes) {
      encoder = encoder.line(`Notes: ${p.notes || ""}`)
    }
  })

  encoder = printBorder(encoder, width, { noSpacing: true })
  if (trx.notes) {
    encoder = encoder.line(`Notes: ${trx.notes}`)
  }
  encoder = encoder
    .align("center")
    .line(`Cashier: [${activeUser}]`)
    .bold(true)
    .line(trx.customerName)
    .newline()

  if (trx.orderType) {
    encoder = printBorder(encoder, width, { noSpacing: true })
    encoder = encoder
      .bold(true)
      .line(`Order Type: ${trx.orderType}`)
      .bold(false)
    encoder = printBorder(encoder, width, { noSpacing: true })
    encoder = encoder.newline()
  }

  if (trx.queueNumber) {
    encoder = encoder
      .align("center")
      .height(2)
      .line(trx.queueNumber)
      .height(1)
      .newline()
  }

  if (trx.location?.birEnabled) {
    encoder = encoder
      .align("center")
      .line("THIS DOCUMENT IS NOT VALID FOR CLAIM OF INPUT TAX")
  }

  return encoder.newline().newline().newline().cut().encode()
}

export const encodeFullKitchenPrinter = (
  trx: KitchenPrinterItem,
  activeUser: string,
  business: Business,
  currency?: string,
  kitchenPrinterOptions?: KitchenPrinterOptions,
  encoderOptions: ThermalPrinterEncoderOption = {
    language: "esc-pos",
    columns: 32,
  }
) => {
  const width = 32
  let encoder = new ThermalPrinterEncoder(encoderOptions)
    .initializeWithMultiByte()
    .align("center")
    .bold(true)

  if (kitchenPrinterOptions?.type === "REPRINT") {
    encoder = encoder.line("*** REPRINT ***")
  } else if (kitchenPrinterOptions?.type === "EDITED") {
    encoder = encoder.line("*** EDITED ***")
  }

  if (trx.orderNumber) {
    encoder = encoder.line(`#${trx.orderNumber}`)
  }

  encoder = encoder
    .line(business.name)
    .line(trx.customerName || "")
    .newline()
    .align("left")
    .bold(false)
    .line(moment(Date.now()).format("D MMM YYYY h:mm a"))
    .line(`Cashier: [${activeUser}]`)

  if (trx.location?.birEnabled) {
    const originalSeriesNumber = trx.receiptHistory?.find(
      (el: any) => el.type === "ORIGINAL"
    )?.seriesNumber
    encoder = encoder.line(`Trans No.: ${originalSeriesNumber}`)
  }

  encoder = printBorder(encoder, width, { noSpacing: true })
  encoder = printSplitTable(encoder, width, 8, [
    ["Item(s)", trx.products.length.toString()],
  ])
  encoder = printBorder(encoder, width, { noSpacing: true })

  trx.products.map((p) => {
    let sellingPrice = p.sellingPrice
    if (p.taxIncluded && p.originalSellingPrice)
      sellingPrice = p.originalSellingPrice

    const totalAddOnPrice =
      p.orderedProductAddOns?.reduce(
        (acc, v) => acc + v.sellingPrice * v.quantity,
        0
      ) || 0
    sellingPrice += totalAddOnPrice

    let name = `${p.quantity} x ${p.name}`

    encoder = printSplitTable(encoder, width, width / 2, [
      [
        name,
        formatNumberToCurrency(currency, p.quantity * sellingPrice, "code"),
      ],
    ])

    if (p.productOptionValues) {
      if (kitchenPrinterOptions?.variant === "ONE_LINE") {
        encoder = encoder.line(
          p.productOptionValues.map((v) => `${v.name}`).join(", ")
        )
      } else {
        p.productOptionValues.forEach((v) => {
          encoder = encoder.line(`${v.name}: ${v.productOptionName}`)
        })
      }
    }

    if (p.orderedProductAddOns?.length) {
      encoder = encoder.line("Add-ons")
      p.orderedProductAddOns.forEach((v) => {
        encoder = printSplitTable(encoder, width, width / 2, [
          [
            `- ${v.name}`,
            formatNumberToCurrency(
              currency,
              v.quantity * v.sellingPrice,
              "code"
            ),
          ],
        ])
      })
    }

    if (p.notes) {
      encoder = encoder.line(`Notes: ${p.notes || ""}`)
    }
  })

  encoder = printBorder(encoder, width, { noSpacing: true })

  const allTaxes = trx.taxes?.reduce((acc, curr) => acc + curr.amount, 0)
  const addedTaxes = trx.taxes
    ?.filter((t) => t.type === "ADDED")
    .reduce((acc, curr) => acc + curr.amount, 0)
  encoder = printSplitTable(encoder, width, 16, [
    ["Discount", formatNumberToCurrency(currency, trx.discount, "code") || "0"],
    [
      "Service Charge",
      formatNumberToCurrency(currency, trx.serviceCharge, "code") || "0",
    ],
    // ["Tax", formatNumberToCurrency(currency, allTaxes, "code") || "0"],
  ])

  if (trx.taxes?.length) {
    encoder = printSplitTable(
      encoder,
      width,
      16,
      trx.taxes.map((t) => [
        `${t.name}`,
        formatNumberToCurrency(currency, t.amount, "code") || "0",
      ])
    )
  }

  encoder = printBorder(encoder, width, { noSpacing: true })
  encoder = printSplitTable(encoder, width, 10, [
    [
      "Subtotal",
      formatNumberToCurrency(
        currency,
        (trx.totalCartAmount || 0) -
          (trx.discount || 0) +
          (trx.serviceCharge || 0) +
          (addedTaxes || 0),
        "code"
      ) || "0",
    ],
  ])
  encoder = printBorder(encoder, width, { noSpacing: true })
  encoder = encoder.line(`Notes: ${trx.notes || ""}`).newline()

  if (trx.orderType) {
    encoder = printBorder(encoder, width, { noSpacing: true })
    encoder = encoder
      .bold(true)
      .line(`Order Type: ${trx.orderType}`)
      .bold(false)
    encoder = printBorder(encoder, width, { noSpacing: true })
    encoder = encoder.newline()
  }

  if (trx.location?.birEnabled) {
    encoder = encoder
      .align("center")
      .line("THIS DOCUMENT IS NOT VALID FOR CLAIM OF INPUT TAX")
  }

  return encoder.newline().newline().cut("partial").encode()
}
